// src/features/social/UserProfileOffcanvas.jsx
//
import React, {
  useState, useEffect, useCallback, useRef, memo,
} from "react";
import Offcanvas from "../../components/common/SwipeableOffcanvas.jsx";
import { getAuth }   from "firebase/auth";
import QRCode        from "qrcode";

import { getStorage, ref, uploadString, getDownloadURL } from "firebase/storage";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { v4 as uuidv4 } from "uuid";

import callFirebaseFunction from "../../utils/firebaseFunctionCaller.js";
import PerfilNone           from "../../Assets/images/imagePerfilNull.svg";
import Style                from "../../styles/features/social/UserProfileOffcanvas.module.css";
// Layout do perfil reutilizado do dashboard (profileBody) para manter o
// mesmo visual ao abrir o perfil de outro usuário.
import PB                   from "../../styles/features/dashboard/PerfilUser.module.css";

// Ajuste o path conforme sua estrutura de projeto:
import { OfensivaCard, NivelCard } from "../vita/components/XpVitaCardCompact.jsx";

import { WeeklyXpChart, XpStatRow } from "./WeeklyXpChart.jsx";
import OffcanvasAddAmigos from "../dashboard/section/perfilUser/components/OffcanvasAddAmigos.jsx";
import { linkDoPerfil, linkPublicoDoPerfil } from "./linksSociais";
import SeloCriador from "../criador/SeloCriador";
import SeloPremium from "../criador/SeloPremium";
import NomeComSelo from "../criador/NomeComSelo";

import { FaCamera } from "react-icons/fa";
import {
  IoArrowBack,
  IoPersonAddOutline,
  IoCheckmarkCircleOutline,
  IoQrCodeOutline,
  IoShareSocialOutline,
  IoCopyOutline,
  IoCloseOutline,
  IoFlame,
  IoPeopleOutline,
  IoSearchOutline,
} from "react-icons/io5";

// ─── Avatar (mini, reutilizável) ──────────────────────────────────────────────

const Avatar = memo(function Avatar({ photoUrl, name, size = 40 }) {
  return (
    <img
      src={photoUrl || PerfilNone}
      alt={name || "Usuário"}
      className={Style.avatar}
      style={{ width: size, height: size }}
      onError={(e) => { e.currentTarget.src = PerfilNone; }}
    />
  );
});

// ─── QRModal ──────────────────────────────────────────────────────────────────

function QRModal({ uid, name, onClose }) {
  const [qrSrc, setQrSrc]   = useState("");
  const [copied, setCopied] = useState(false);
  // Deep link real: abre o APP direto no perfil da pessoa (App Link/Universal
  // Link); sem app instalado cai na web autenticada no mesmo destino.
  /* O QR é lido de aparelho para aparelho, por quem quase sempre TEM o app:
     deep link, que abre direto. O compartilhar vai para fora e usa o link
     público, que tem prévia e funciona sem conta. */
  const profileUrl          = linkDoPerfil(uid);
  const linkParaFora        = linkPublicoDoPerfil(uid);

  useEffect(() => {
    QRCode.toDataURL(profileUrl, { width: 220, margin: 1, color: { dark: "#00095C", light: "#ffffff" } })
      .then((url) => setQrSrc(url))
      .catch(console.error);
  }, [profileUrl]);

  async function handleShare() {
    try { await navigator.share({ url: linkParaFora, title: `LesVita — ${name}` }); }
    catch { handleCopy(); }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(linkParaFora);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.error("Clipboard error:", err); }
  }

  return (
    <div className={Style.modalOverlay} onClick={onClose}>
      <div className={Style.qrModal} onClick={(e) => e.stopPropagation()}>
        <div className={Style.qrModalHeader}>
          <span className={Style.qrModalTitle}>Compartilhar perfil</span>
          <button className={Style.qrModalClose} onClick={onClose}><IoCloseOutline size={20} /></button>
        </div>
        <div className={Style.qrModalBody}>
          {qrSrc ? <img src={qrSrc} alt="QR Code" className={Style.qrImage} /> : <div className={Style.qrPlaceholder} />}
          <p className={Style.qrName}>{name}</p>
          <p className={Style.qrUrl}>{profileUrl}</p>
        </div>
        <div className={Style.qrModalActions}>
          <button className={Style.qrActionBtn} onClick={handleShare}><IoShareSocialOutline size={18} />Enviar link</button>
          <button className={`${Style.qrActionBtn} ${copied ? Style.qrActionBtnCopied : ""}`} onClick={handleCopy}>
            <IoCopyOutline size={18} />{copied ? "Copiado!" : "Copiar link"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ProfileSkeleton ──────────────────────────────────────────────────────────

function ProfileSkeleton() {
  return (
    <div className={Style.skeleton}>
      <div className={`${Style.skBlock} ${Style.skBanner}`} />
      <div className={Style.skBody}>
        <div className={Style.skRow}>
          <div className={`${Style.skBlock} ${Style.skCircle}`} />
          <div className={Style.skStatGroup}>
            <div className={`${Style.skBlock} ${Style.skStatVal}`} />
            <div className={`${Style.skBlock} ${Style.skStatLabel}`} />
          </div>
          <div className={Style.skStatGroup}>
            <div className={`${Style.skBlock} ${Style.skStatVal}`} />
            <div className={`${Style.skBlock} ${Style.skStatLabel}`} />
          </div>
        </div>
        <div className={`${Style.skBlock} ${Style.skBtn}`} />
        {/* Skeleton mini cards */}
        <div style={{ display: "flex", gap: 12 }}>
          <div className={`${Style.skBlock} ${Style.skCard}`} style={{ flex: 1, height: 160 }} />
          <div className={`${Style.skBlock} ${Style.skCard}`} style={{ flex: 1, height: 160 }} />
        </div>
        <div className={`${Style.skBlock} ${Style.skCard}`} style={{ height: 140 }} />
      </div>
    </div>
  );
}

// ─── ProfileView ──────────────────────────────────────────────────────────────

function ProfileView({ uid, onPush, onTitleChange }) {
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [isFollowing,    setIsFollowing]    = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followLoading,  setFollowLoading]  = useState(false);
  const [showQR,         setShowQR]         = useState(false);

  // ── Próprio perfil: paridade com o dashboard (adicionar amigos + editar foto)
  const [showAddAmigos,        setShowAddAmigos]        = useState(false);
  const [showPhotoOffcanvas,   setShowPhotoOffcanvas]   = useState(false);
  const [showPhotoReview,      setShowPhotoReview]      = useState(false);
  const [capturedProfileImage, setCapturedProfileImage] = useState(null);
  const [isUploadingPhoto,     setIsUploadingPhoto]     = useState(false);

  const myUid        = getAuth().currentUser?.uid;
  const isOwnProfile = uid === myUid;
  const mountedRef   = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [uid]);

  async function load() {
    setLoading(true);
    try {
      const res = await callFirebaseFunction("getUserSocialProfile", { targetUid: uid });
      if (!mountedRef.current) return;
      setData(res);
      setIsFollowing(!!res.isFollowing);
      setFollowersCount(res.followersCount ?? 0);
      setFollowingCount(res.followingCount ?? 0);
      onTitleChange?.(res.name);
    } catch (err) {
      console.error("getUserSocialProfile:", err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  async function handleFollow() {
    if (followLoading) return;
    setFollowLoading(true);
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setFollowersCount((c) => wasFollowing ? c - 1 : c + 1);
    try {
      const fn = wasFollowing ? "unfollowUser" : "followUser";
      await callFirebaseFunction(fn, { targetUid: uid });
    } catch (err) {
      setIsFollowing(wasFollowing);
      setFollowersCount((c) => wasFollowing ? c + 1 : c - 1);
      console.error("follow/unfollow:", err);
    } finally {
      if (mountedRef.current) setFollowLoading(false);
    }
  }

  function openList(initialTab) { onPush({ type: "list", uid, initialTab }); }

  // ── Foto de perfil (somente próprio perfil — espelha o fluxo do dashboard) ──
  const openPhotoOffcanvas = () => {
    setCapturedProfileImage(null);
    setShowPhotoReview(false);
    setShowPhotoOffcanvas(true);
  };

  const takeProfilePicture = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90, allowEditing: true, width: 1024, height: 1024,
        resultType: CameraResultType.DataUrl, source: CameraSource.Camera,
      });
      if (image?.dataUrl) {
        setCapturedProfileImage(image.dataUrl);
        setShowPhotoOffcanvas(false);
        setShowPhotoReview(true);
      }
    } catch (error) { console.error("Erro ao tirar foto:", error); }
  };

  const chooseProfileFromGallery = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90, allowEditing: true, width: 1024, height: 1024,
        resultType: CameraResultType.DataUrl, source: CameraSource.Photos,
      });
      if (image?.dataUrl) {
        setCapturedProfileImage(image.dataUrl);
        setShowPhotoOffcanvas(false);
        setShowPhotoReview(true);
      }
    } catch (error) { console.error("Erro ao escolher foto:", error); }
  };

  const handleSaveProfilePhoto = async () => {
    if (!capturedProfileImage) return;
    try {
      setIsUploadingPhoto(true);
      const storage      = getStorage();
      const storageRef   = ref(storage, `profile_images/${myUid}/${uuidv4()}.jpg`);
      const uploadResult = await uploadString(storageRef, capturedProfileImage, "data_url");
      const downloadURL  = await getDownloadURL(uploadResult.ref);
      await callFirebaseFunction("updateUserProfilePhoto", { imageUrl: downloadURL });
      if (mountedRef.current) {
        setData((d) => (d ? { ...d, photoUrl: downloadURL } : d));
        setShowPhotoReview(false);
        setCapturedProfileImage(null);
      }
    } catch (error) {
      console.error("Erro ao salvar foto:", error);
      alert("Não foi possível salvar sua foto. Tente novamente.");
    } finally {
      if (mountedRef.current) setIsUploadingPhoto(false);
    }
  };

  if (loading) return <ProfileSkeleton />;
  if (!data)   return <p className={Style.errorMsg}>Não foi possível carregar o perfil.</p>;

  // Dados transformados para os mini cards
  const ofensivaData = { STREAK_DIAS: data.streakDias, MELHOR_STREAK: data.streakDias };
  const nivelData    = {
    LEVEL:            data.level,
    CURRENT_LEVEL_XP: data.currentLevelXp,
    NEXT_LEVEL_XP:    data.nextLevelXp,
    TOTAL_XP:         data.totalXp,
  };

  return (
    <div className={Style.profileView}>

      {/* ── Foto + stats sociais (layout idêntico ao profileBody) ── */}
      <div className={PB.photoStatsRow}>
        <div className={PB.photoWrap}>
          <div
            className={PB.photoCircle}
            style={{ backgroundImage: `url(${data.photoUrl || PerfilNone})` }}
          >
            {data.level != null && (
              <span className={PB.levelBadgeLarge}>{data.level}</span>
            )}
            {/* só o dono edita a própria foto */}
            {isOwnProfile && (
              <button type="button" className={PB.editPhotoBtn} onClick={openPhotoOffcanvas}>
                <FaCamera className={PB.camIcon} />
              </button>
            )}
          </div>
        </div>
        <div className={PB.profileInfo}>
          <div className={PB.profileNameLine}>
            <h5 className={PB.profileName}>{data.name}</h5>
            {data.criador && <SeloCriador size={20} glow={false} />}
            {data.premium && !data.criador && <SeloPremium size={20} glow={false} />}
          </div>
          <div className={PB.socialStats}>
          <button className={PB.statBtn} onClick={() => openList("followers")}>
            <span className={PB.statValue}>{followersCount.toLocaleString("pt-BR")}</span>
            <span className={PB.statLabel}>seguidores</span>
          </button>
          <div className={PB.statDivider} />
          <button className={PB.statBtn} onClick={() => openList("following")}>
            <span className={PB.statValue}>{followingCount.toLocaleString("pt-BR")}</span>
            <span className={PB.statLabel}>seguindo</span>
          </button>
          </div>
        </div>
      </div>

      {/* ── Ações: próprio = adicionar amigos; outro = seguir. QR sempre. ── */}
      <div className={PB.socialActionsRow}>
        {isOwnProfile ? (
          <button className={PB.addFriendBtn} onClick={() => setShowAddAmigos(true)}>
            <IoPersonAddOutline size={18} /><span>Adicionar amigos</span>
          </button>
        ) : (
          <button
            className={`${PB.addFriendBtn} ${isFollowing ? Style.followBtnActive : ""}`}
            onClick={handleFollow}
            disabled={followLoading}
          >
            {isFollowing
              ? <><IoCheckmarkCircleOutline size={17} /> Seguindo</>
              : <><IoPersonAddOutline size={17} /> Seguir</>
            }
          </button>
        )}
        <button
          className={PB.qrBtn}
          onClick={() => (isOwnProfile ? setShowAddAmigos(true) : setShowQR(true))}
          title="Compartilhar perfil"
        >
          <IoQrCodeOutline size={20} />
        </button>
      </div>

      {/* ── Mini cards: Ofensiva + Nível ── */}
      <div className={PB.miniCardsRow}>
        <OfensivaCard data={ofensivaData} isActive={true} onOpenVita={() => {}} />
        <NivelCard    data={nivelData}    isActive={true} onOpenVita={() => {}} />
      </div>

      {/* ── Progresso semanal ── */}
      {data.weeklyXp?.length > 0 && (
        <div className={PB.weeklySection}>
          <p className={PB.weeklySectionTitle}>Progresso semanal</p>
          <div className={PB.chartCard}>
            <WeeklyXpChart data={data.weeklyXp} chartStyle={PB.weekChart} />
            <XpStatRow
              weekly={data.weeklyXpTotal}
              monthly={data.monthlyXpTotal}
              total={data.totalXp}
              styles={PB}
            />
          </div>
        </div>
      )}

      <div style={{ height: 80 }} />

      {/* ── QR Modal (compartilhar perfil de outro usuário) ── */}
      {showQR && (
        <QRModal uid={uid} name={data.name} onClose={() => setShowQR(false)} />
      )}

      {/* ── Próprio perfil: Adicionar amigos ── */}
      {isOwnProfile && (
        <OffcanvasAddAmigos
          show={showAddAmigos}
          onClose={() => setShowAddAmigos(false)}
          uid={myUid}
          name={data.name}
          onOpenProfile={(targetUid) => { setShowAddAmigos(false); onPush({ type: "profile", uid: targetUid }); }}
        />
      )}

      {/* ── Próprio perfil: escolha de foto ── */}
      {isOwnProfile && (
        <Offcanvas
          show={showPhotoOffcanvas}
          onHide={() => setShowPhotoOffcanvas(false)}
          placement="bottom"
          className={PB.offcanvasPhotoProfile}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title style={{ color: "var(--color-primary)" }}>Escolha uma opção</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className={PB.offcanvasPhotoBody}>
            <div className={PB.photoOptions}>
              <button className={PB.photoOptionButton} onClick={takeProfilePicture}>Tirar uma foto</button>
              <button
                className={`${PB.photoOptionButton} ${PB.photoOptionButtonSecondary}`}
                onClick={chooseProfileFromGallery}
              >
                Escolher da galeria
              </button>
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      )}

      {/* ── Próprio perfil: review da foto ── */}
      {isOwnProfile && (
        <Offcanvas
          show={showPhotoReview}
          onHide={() => { setShowPhotoReview(false); setCapturedProfileImage(null); }}
          placement="end"
          className={PB.offcanvasPhotoReview}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title style={{ color: "var(--color-primary)" }}>Prévia da sua foto</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className={PB.offcanvasPhotoBody}>
            {capturedProfileImage && (
              <div className={PB.photoPreviewWrapper}>
                <div className={PB.photoPreview} style={{ backgroundImage: `url(${capturedProfileImage})` }} />
                <div className={PB.photoActions}>
                  <button
                    className={PB.btnRefazer}
                    onClick={() => { setCapturedProfileImage(null); setShowPhotoReview(false); setShowPhotoOffcanvas(true); }}
                    disabled={isUploadingPhoto}
                  >
                    Refazer
                  </button>
                  <button className={PB.btnSalvar} onClick={handleSaveProfilePhoto} disabled={isUploadingPhoto}>
                    {isUploadingPhoto ? "Salvando..." : "Salvar foto"}
                  </button>
                </div>
              </div>
            )}
          </Offcanvas.Body>
        </Offcanvas>
      )}
    </div>
  );
}

// ─── UserListItem ─────────────────────────────────────────────────────────────

function UserListItem({ user, onPress }) {
  return (
    <button className={Style.userListItem} onClick={() => onPress(user.uid)}>
      <Avatar photoUrl={user.photoUrl} name={user.name} size={44} />
      <div className={Style.userListInfo}>
        <NomeComSelo
          criador={user.criador === true}
          premium={user.premium === true}
          className={Style.userListName}
        >
          {user.name}
        </NomeComSelo>
        <span className={Style.userListLevel}>Nível {user.level}</span>
      </div>
      <IoArrowBack size={16} className={Style.userListArrow} />
    </button>
  );
}

// ─── SocialListView ───────────────────────────────────────────────────────────

function SocialListView({ uid, initialTab = "followers", onPushProfile }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [busca,     setBusca]     = useState("");
  const [foraDaLista, setForaDaLista] = useState([]);
  const [buscandoFora, setBuscandoFora] = useState(false);
  const mountedRef                = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => { loadList(); }, [uid, activeTab]);

  // Trocar de aba não carrega a busca da aba anterior.
  useEffect(() => { setBusca(""); setForaDaLista([]); }, [activeTab]);

  /* A busca filtra o que JÁ está carregado (instantâneo, sem rede) e, em
     paralelo, procura no app inteiro — é o pedido de "buscar entre os da lista
     ou fora dela". A chamada global é debounced e só sai com 2+ caracteres:
     `searchUsersByName` é uma consulta de servidor e não pode disparar a cada
     tecla. Quem já está na lista é removido do resultado global, senão a mesma
     pessoa apareceria dos dois lados do separador. */
  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 2) {
      setForaDaLista([]);
      setBuscandoFora(false);
      return undefined;
    }
    setBuscandoFora(true);
    const id = setTimeout(async () => {
      try {
        const res = await callFirebaseFunction("searchUsersByName", { query: termo });
        if (!mountedRef.current) return;
        const naLista = new Set(users.map((u) => u.uid));
        setForaDaLista((res?.users || []).filter((u) => u.uid !== uid && !naLista.has(u.uid)));
      } catch (err) {
        console.error("searchUsersByName:", err);
        if (mountedRef.current) setForaDaLista([]);
      } finally {
        if (mountedRef.current) setBuscandoFora(false);
      }
    }, 350);
    return () => clearTimeout(id);
  }, [busca, users, uid]);

  const termo = busca.trim().toLocaleLowerCase();
  const visiveis = termo
    ? users.filter((u) => String(u.name || "").toLocaleLowerCase().includes(termo))
    : users;

  async function loadList() {
    setLoading(true);
    setUsers([]);
    try {
      const res = await callFirebaseFunction("getSocialList", { targetUid: uid, listType: activeTab });
      if (mountedRef.current) setUsers(res.users || []);
    } catch (err) {
      console.error("getSocialList:", err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  return (
    <div className={Style.listView}>
      <div className={Style.listTabs}>
        {["followers", "following"].map((tab) => (
          <button
            key={tab}
            className={`${Style.listTab} ${activeTab === tab ? Style.listTabActive : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "followers" ? "Seguidores" : "Seguindo"}
          </button>
        ))}
      </div>
      <div className={Style.listBusca}>
        <IoSearchOutline size={18} className={Style.listBuscaIcone} />
        <input
          className={Style.listBuscaCampo}
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar pessoa"
          aria-label="Buscar pessoa"
        />
        {busca && (
          <button
            type="button"
            className={Style.listBuscaLimpar}
            onClick={() => setBusca("")}
            aria-label="Limpar busca"
          >
            <IoCloseOutline size={16} />
          </button>
        )}
      </div>

      <div className={Style.listBody}>
        {loading && (
          <div className={Style.listSkeleton}>
            {[1, 2, 3, 4].map((i) => <div key={i} className={`${Style.skBlock} ${Style.skUserRow}`} />)}
          </div>
        )}
        {!loading && users.length === 0 && !termo && (
          <div className={Style.listEmpty}>
            <IoPeopleOutline size={40} opacity={0.3} />
            <p>{activeTab === "followers" ? "Nenhum seguidor ainda." : "Não está seguindo ninguém ainda."}</p>
          </div>
        )}
        {!loading && visiveis.map((user) => (
          <UserListItem key={user.uid} user={user} onPress={onPushProfile} />
        ))}

        {/* Separador: o que veio da lista acaba aqui; abaixo é gente que não
            segue nem é seguida por este perfil. Sem a linha, o resultado
            global parecia parte da lista de seguidores. */}
        {!loading && termo.length >= 2 && (foraDaLista.length > 0 || buscandoFora) && (
          <div className={Style.listSeparador}>
            <span>{buscandoFora ? "procurando no app…" : "outras pessoas no app"}</span>
          </div>
        )}
        {!loading && foraDaLista.map((user) => (
          <UserListItem key={`fora-${user.uid}`} user={user} onPress={onPushProfile} />
        ))}

        {!loading && termo && visiveis.length === 0 && foraDaLista.length === 0 && !buscandoFora && (
          <div className={Style.listEmpty}>
            <IoPeopleOutline size={40} opacity={0.3} />
            <p>Ninguém encontrado para “{busca.trim()}”.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── UserProfileOffcanvas (shell principal) ───────────────────────────────────

export default function UserProfileOffcanvas({
  show, current, canGoBack, onBack, onClose, onPush,
}) {
  const [headerTitle, setHeaderTitle] = useState("");

  useEffect(() => {
    if (current?.type === "list") {
      setHeaderTitle(current.initialTab === "followers" ? "Seguidores" : "Seguindo");
    } else {
      setHeaderTitle("");
    }
  }, [current?.type, current?.uid, current?.initialTab]);

  function handlePushProfile(uid) { onPush({ type: "profile", uid }); }

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" className={Style.offcanvas}>
      <Offcanvas.Body className={Style.offcanvasBody}>
        {/* ── Header sticky ── */}
        <div className={Style.header}>
          <button className={Style.headerBack} onClick={canGoBack ? onBack : onClose}>
            <IoArrowBack size={20} />
          </button>
          <span className={Style.headerTitle}>{headerTitle}</span>
          <button className={Style.headerClose} onClick={onClose}>
            <IoCloseOutline size={20} />
          </button>
        </div>

        {/* ── Conteúdo ── */}
        {current?.type === "profile" && (
          <ProfileView
            key={current.uid}
            uid={current.uid}
            onPush={onPush}
            onTitleChange={setHeaderTitle}
          />
        )}
        {current?.type === "list" && (
          <SocialListView
            key={`${current.uid}-${current.initialTab}`}
            uid={current.uid}
            initialTab={current.initialTab}
            onPushProfile={handlePushProfile}
          />
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
